import React from 'react';
import { AlertTriangle, CalendarCheck, ListChecks, Signature } from 'lucide-react';
import { PriorityChart, ProjectStatusChart } from '../../components/ReportCharts';
import {
    Alert,
    Card,
    CardBody,
    CardHeader,
    DataTable,
    StatCard,
    StatusBadge,
} from '../../components/ui';

/*
 * NOTE: this surface is not yet wired to an Excom analytics endpoint — the
 * figures below are placeholders. They are labelled as such in the UI so no one
 * mistakes them for live reporting. Replace with the API response when it exists.
 */
const SAMPLE = {
    stats: [
        { label: 'Total meetings', value: 24, icon: CalendarCheck, tone: 'primary' },
        { label: 'Open actions', value: 42, icon: ListChecks, tone: 'info' },
        { label: 'Overdue actions', value: 12, icon: AlertTriangle, tone: 'danger' },
        { label: 'Pending approvals', value: 5, icon: Signature, tone: 'warning' },
    ],
    actionPriority: [
        { priority: 'critical', count: 5 },
        { priority: 'high', count: 12 },
        { priority: 'medium', count: 18 },
        { priority: 'low', count: 7 },
    ],
    meetingStatus: [
        { status: 'completed', count: 18 },
        { status: 'in_progress', count: 2 },
        { status: 'scheduled', count: 4 },
    ],
    departments: [
        { department: 'Finance', count: 10, status: 'completed' },
        { department: 'Credit', count: 8, status: 'pending' },
        { department: 'ICT', count: 15, status: 'completed' },
        { department: 'Operations', count: 12, status: 'completed' },
        { department: 'Business', count: 9, status: 'in_progress' },
    ],
    overdue: [
        { id: 1, title: 'Finalise Q1 budget review', ref: 'EXCOM-2024-03-12-001', days: 5, owner: 'Finance' },
        { id: 2, title: 'Submit vendor risk assessment', ref: 'EXCOM-2024-03-12-004', days: 3, owner: 'ICT' },
        { id: 3, title: 'Approve branch expansion plan', ref: 'EXCOM-2024-02-28-002', days: 11, owner: 'Business' },
        { id: 4, title: 'Close AML audit findings', ref: 'EXCOM-2024-02-28-007', days: 8, owner: 'Compliance' },
    ],
};

const ExcomDashboard = () => (
    <div className="space-y-4">
        <Alert variant="info">
            These figures are illustrative placeholders. They will show live values once the Excom
            analytics endpoint is connected.
        </Alert>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {SAMPLE.stats.map((s) => (
                <StatCard key={s.label} {...s} caption="Sample figure" />
            ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
                <CardHeader title="Action priority" subtitle="Open action points by urgency" />
                <CardBody>
                    <PriorityChart data={SAMPLE.actionPriority} />
                </CardBody>
            </Card>

            <Card>
                <CardHeader title="Meeting status" subtitle="Sessions by current state" />
                <CardBody>
                    <ProjectStatusChart data={SAMPLE.meetingStatus} />
                </CardBody>
            </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
                <CardHeader title="Department submissions" />
                <DataTable
                    columns={[
                        {
                            key: 'department',
                            header: 'Department',
                            render: (d) => <span className="font-medium text-ink-900">{d.department}</span>,
                        },
                        {
                            key: 'count',
                            header: 'Submissions',
                            align: 'right',
                            render: (d) => <span className="tabular text-ink-700">{d.count}</span>,
                        },
                        {
                            key: 'status',
                            header: 'Latest status',
                            align: 'right',
                            render: (d) => <StatusBadge status={d.status} />,
                        },
                    ]}
                    rows={SAMPLE.departments}
                    rowKey={(d) => d.department}
                />
            </Card>

            <Card>
                <CardHeader title="Overdue action points" subtitle="Oldest first" />
                <ul className="divide-y divide-[var(--divider-color)]">
                    {SAMPLE.overdue
                        .slice()
                        .sort((a, b) => b.days - a.days)
                        .map((item) => (
                            <li key={item.id} className="flex items-center gap-3 px-5 py-3">
                                <span className="min-w-0 flex-1">
                                    <span className="block text-base font-medium text-ink-900 truncate">
                                        {item.title}
                                    </span>
                                    <span className="block text-micro text-ink-500 font-mono truncate">
                                        {item.ref}
                                    </span>
                                </span>
                                <span className="text-label text-ink-600 whitespace-nowrap">{item.owner}</span>
                                <span className="badge badge-danger shrink-0 tabular">
                                    {item.days}d late
                                </span>
                            </li>
                        ))}
                </ul>
            </Card>
        </div>
    </div>
);

export default ExcomDashboard;
